// Mål-koordinat-kalkulator (Grid/MGRS) — regner ut målkoordinat fra en
// observatørs polare innmelding (peiling + avstand), og konverterer mellom
// lat/lon og MGRS. Ren, testbar logikk. Brukes av ildstøtte/mål-innmelding:
// «FO ser mål 2200 mils, 1500 m» → MGRS å gi til skytteren.
//
// NB: 'mgrs' er dual-package (CJS i Node, ESM i Vite) — samme namespace-import +
// .default-fallback som osint.ts (ellers krasjer navngitt ESM-import i Node).
import * as mgrsNs from 'mgrs';
import { project, haversineM } from './geo.js';
const _mgrs = mgrsNs;
const mgrsToPoint = _mgrs.toPoint ?? _mgrs.default.toPoint;
const mgrsForward = _mgrs.forward ?? _mgrs.default.forward;
// ── Vinkel: grader ↔ mils (NATO 6400) ───────────────────────────────────────
export const MILS_PER_CIRCLE = 6400;
export function degToMils(deg) {
    return norm360(deg) / 360 * MILS_PER_CIRCLE;
}
export function milsToDeg(mils) {
    return ((mils % MILS_PER_CIRCLE + MILS_PER_CIRCLE) % MILS_PER_CIRCLE) / MILS_PER_CIRCLE * 360;
}
function norm360(d) { return ((d % 360) + 360) % 360; }
// ── Peiling mellom to punkter (invers av geo.project) ───────────────────────
/** Initial peiling (grader, 0=nord, med klokka) fra `a` til `b`. */
export function bearingDeg(a, b) {
    const φ1 = rad(a.lat), φ2 = rad(b.lat), Δλ = rad(b.lon - a.lon);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return norm360(deg(Math.atan2(y, x)));
}
function rad(d) { return (d * Math.PI) / 180; }
function deg(r) { return (r * 180) / Math.PI; }
// ── MGRS ↔ lat/lon ──────────────────────────────────────────────────────────
/** lat/lon → MGRS. `precision` = antall siffer per akse (5 = 1 m, 4 = 10 m …). */
export function latLonToMgrs(p, precision = 5) {
    try {
        return mgrsForward([p.lon, p.lat], precision);
    }
    catch {
        return '';
    }
}
/**
 * MGRS for VISNING — gruppert slik referansen leses opp: sone+bånd, 100 km-rute,
 * easting, northing. `32VNM0234566890` → `32V NM 02345 66890`.
 *
 * Eier-bestilling, ordrett: «Kan du legge mellomrom mellom de 5 første og 5 siste
 * tallen. Altså mellom 1 og 5.» Uten skillet må operatøren telle siffer for å
 * finne hvor eastingen slutter — og det er nettopp under tidspress at feilen
 * koster. Grupperingen er også måten griden faktisk leses over samband.
 *
 * VIKTIG: dette er en ren visningsfunksjon. `latLonToMgrs` returnerer fortsatt
 * kompakt form, fordi den verdien går i wire og eksport (CoT, GeoJSON,
 * meldingstekst) og i `mgrsToLatLon`. Formatér ved utskrift, aldri ved lagring.
 *
 * Ugjenkjennelig inndata returneres uendret — en visningshjelper skal ikke
 * kaste, og skal ikke skjule at strengen var noe annet enn ventet.
 */
export function formatMgrs(mgrs) {
    const s = mgrs.replace(/\s+/g, '').toUpperCase();
    // Sone (1-2 siffer) + båndbokstav + 100 km-rute (2 bokstaver) + like mange
    // siffer per akse. I og O er ikke i bruk i MGRS.
    const m = /^(\d{1,2})([C-HJ-NP-X])([A-HJ-NP-Z]{2})(\d*)$/.exec(s);
    if (!m)
        return mgrs;
    const [, sone, band, rute, siffer] = m;
    if (siffer.length === 0)
        return `${sone}${band} ${rute}`;
    // Odde sifferlengde = ugyldig MGRS (aksene har alltid like mange siffer).
    if (siffer.length % 2 !== 0)
        return mgrs;
    const halv = siffer.length / 2;
    return `${sone}${band} ${rute} ${siffer.slice(0, halv)} ${siffer.slice(halv)}`;
}
/** MGRS → lat/lon (null ved ugyldig streng). */
export function mgrsToLatLon(mgrs) {
    try {
        const [lon, lat] = mgrsToPoint(mgrs.replace(/\s+/g, '').toUpperCase());
        if (!Number.isFinite(lat) || !Number.isFinite(lon))
            return null;
        return { lat, lon };
    }
    catch {
        return null;
    }
}
/** Regn ut målkoordinat fra en polar innmelding. Grid gis som MGRS (1 m). */
export function polarTarget(input) {
    const bDeg = input.unit === 'mils' ? milsToDeg(input.bearing) : norm360(input.bearing);
    const rangeM = Math.max(0, input.rangeM);
    const target = project(input.observer, bDeg, rangeM);
    return {
        target,
        mgrs: latLonToMgrs(target, 5),
        bearingDeg: bDeg,
        bearingMils: Math.round(degToMils(bDeg)),
        rangeM,
    };
}
/** Invers: peiling + avstand fra observatør til et kjent mål (til kontroll/BDA). */
export function polarFromPoints(observer, target) {
    const bDeg = bearingDeg(observer, target);
    return { bearingDeg: bDeg, bearingMils: Math.round(degToMils(bDeg)), rangeM: Math.round(haversineM(observer, target)) };
}
//# sourceMappingURL=targeting.js.map